<script setup lang="ts">
import { ref, watch, onMounted } from "vue";
import { useEquipment } from "@/composables/tasks/useEquipment";
import { useEquipmentForm } from "@/composables/tasks/useEquipmentForm";
import EquipmentConfigForm from "@/components/v2/Tasks/Misc/Equipment/Forms/EquipmentConfigForm.vue";
import { EquipmentConfiguration } from "@/types/v2";

// ===================================================================
// State
// ===================================================================
const showEditForm = ref(false);
const editingConfigId = ref<string | number | null>(null);

// ===================================================================
// Composables
// ===================================================================
// Equipment operations
const {
  getEquipmentConfigurations,
  formatValue,
  getOptionLabel,
  removeEquipmentConfiguration,
  hasWeightIdRequirement,
  getWeightEquipmentConfig,
  getWeightId,
  updateConfigValue,
} = useEquipment();

// Helper to convert snake_case to camelCase
const toCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

// Get value from config.values, handling both snake_case and camelCase keys
const getConfigValue = (config: EquipmentConfiguration, optionName: string): any => {
  if (!config.values) return undefined;
  
  // Try camelCase first (since API converts to camelCase)
  const camelKey = toCamelCase(optionName);
  if (camelKey in config.values) {
    return config.values[camelKey];
  }
  
  // Fallback to original snake_case
  if (optionName in config.values) {
    return config.values[optionName];
  }
  
  return undefined;
};

// Equipment form
const {
  initEditForm,
  cancelForm
} = useEquipmentForm();

// ===================================================================
// Actions
// ===================================================================
// Start editing a configuration
const startEditing = (config: EquipmentConfiguration) => {
  editingConfigId.value = config.id;
  initEditForm(config);
  showEditForm.value = true;
};

// Handle form cancellation
const handleCancel = () => {
  cancelForm();
  showEditForm.value = false;
  editingConfigId.value = null;
};

// Handle successful save
const handleSaved = () => {
  showEditForm.value = false;
  editingConfigId.value = null;
};

// Weight ID state and handlers
const weightId = ref<Record<string, string>>({});
const isUpdatingWeightId = ref<Record<string, boolean>>({});

// Get weight ID for a specific config
const getWeightIdForConfig = (config: EquipmentConfiguration): string => {
  if (config.equipmentType.name !== 'Weight') return '';
  if (!config.values || !config.values.weightIdRequired) return '';
  
  // Initialize weightId if it doesn't exist
  if (!('weightId' in config.values)) {
    config.values.weightId = '';
  }
  
  return (config.values.weightId as string) || '';
};

// Initialize weight ID for all Weight configs
const initializeWeightIds = () => {
  getEquipmentConfigurations.value.forEach(config => {
    if (config.equipmentType.name === 'Weight' && config.values?.weightIdRequired) {
      const configId = config.id.toString();
      weightId.value[configId] = getWeightIdForConfig(config);
    }
  });
};

// Update weight ID
const handleWeightIdChange = async (config: EquipmentConfiguration, newValue: string) => {
  if (!config || config.equipmentType.name !== 'Weight') return;

  const configId = config.id.toString();
  isUpdatingWeightId.value[configId] = true;
  
  try {
    await updateConfigValue(config, 'weightId', newValue);
    weightId.value[configId] = newValue;
  } catch (error) {
    console.error('Error updating weight ID:', error);
  } finally {
    isUpdatingWeightId.value[configId] = false;
  }
};

// Watch for weight config changes
onMounted(() => {
  initializeWeightIds();
});

watch(() => getEquipmentConfigurations.value, () => {
  initializeWeightIds();
}, { deep: true, immediate: true });
</script>

<template>
  <div>
    <!-- Edit Configuration Form -->
    <EquipmentConfigForm
        v-if="showEditForm"
        @cancel="handleCancel"
        @saved="handleSaved"
    />

    <!-- Display Equipment Configurations -->
    <div v-else>
      <div v-if="getEquipmentConfigurations && getEquipmentConfigurations.length > 0">
        <div v-for="config in getEquipmentConfigurations" :key="config.id" class="mb-6 border rounded overflow-hidden">
          <!-- Configuration Header -->
          <div class="p-4 bg-primary text-white flex justify-between items-center">
            <div>
              <h3 class="text-lg font-semibold">{{ config.equipmentType.name }}</h3>
              <p v-if="config.equipmentType.description" class="text-sm opacity-80">
                {{ config.equipmentType.description }}
              </p>
            </div>

            <div class="flex space-x-2">
              <button @click="startEditing(config)"
                      class="bg-white text-primary px-3 py-1 rounded text-sm">
                Edit
              </button>
              <button @click="removeEquipmentConfiguration(config)"
                      class="bg-white text-red-500 hover:bg-red-100 px-3 py-1 rounded text-sm">
                Remove
              </button>
            </div>
          </div>

          <!-- Configuration Values -->
          <div class="p-4 bg-white">
            <table class="w-full border-collapse">
              <tbody>
              <tr v-for="option in config.equipmentType.configOptions" :key="option.name" class="border-b">
                <td class="py-3 px-2 font-medium">
                  {{ getOptionLabel(config.equipmentType.configOptions, option.name) }}
                  <span v-if="option.required" class="text-red-500 ml-1">*</span>
                </td>
                <td class="py-3 px-2 text-right">
                  <div class="flex items-center justify-end">
                    <span class="mr-2">
                      {{ formatValue(getConfigValue(config, option.name), option.type) }}
                    </span>
                    <span v-if="option.unit" class="text-gray-500 text-sm">{{ option.unit }}</span>
                    <span v-if="option.modifiable_in_app" class="ml-2">
                      <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Modifiable
                      </span>
                    </span>
                  </div>
                </td>
              </tr>
              <!-- Weight ID field (shown when Weight equipment has weight_id_required = true) -->
              <tr v-if="config.equipmentType.name === 'Weight' && config.values?.weightIdRequired === true" class="border-b">
                <td class="py-3 px-2 font-medium">
                  Weight ID
                  <span class="text-red-500 ml-1">*</span>
                </td>
                <td class="py-3 px-2 text-right">
                  <div class="flex items-center justify-end">
                    <input
                      type="text"
                      :value="weightId[config.id.toString()] || ''"
                      @input="weightId[config.id.toString()] = ($event.target as HTMLInputElement).value"
                      @blur="handleWeightIdChange(config, weightId[config.id.toString()] || '')"
                      @keyup.enter="handleWeightIdChange(config, weightId[config.id.toString()] || '')"
                      :disabled="isUpdatingWeightId[config.id.toString()]"
                      class="border border-gray-300 rounded px-3 py-1 text-right focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="Enter Weight ID"
                    />
                    <span v-if="isUpdatingWeightId[config.id.toString()]" class="ml-2">
                      <svg class="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </span>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div v-else class="p-4 text-center bg-gray-50 border rounded">
        <p class="text-gray-500">No equipment configurations have been set up for this task.</p>
      </div>
    </div>
  </div>
</template>